import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Paper, Text, Container, Grid, Button, Image, Badge, Table, Input, 
  Modal, Collapse, Loader, Title, Group, Card, Anchor, Box, Tooltip,
  TextInput, Accordion, ActionIcon, Divider, ScrollArea, Drawer, Center
} from '@mantine/core';
import { getUserFullDetails } from '../Redux/user/slice';
import { addFieldAPI, getFieldAPI, getEudrReportsAPI } from '../Redux/user/api';
import { generateReport } from '../Redux/eudr/slice';
import { checkFieldHasReport } from '../Redux/eudr/api';
import { toast } from 'react-toastify';
import { 
  IconEdit, IconDeviceFloppy, IconTrash, IconExternalLink, 
  IconSearch, IconChevronDown, IconChevronUp, IconMapPin,
  IconX, IconFilter, IconQrcode, IconUpload
} from '@tabler/icons-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faFile, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { ToastProperties } from '../utils/constants/toastProperties';
import { getFieldTimestamp, groupByYear, UNDATED_LABEL } from '../utils/fieldDates';
import { BASE_URL } from '../Redux/apiConfig';
import { IntlProvider, FormattedMessage, useIntl } from 'react-intl';
import { IconTarget } from '@tabler/icons-react';
import { ResizableMapSection } from './InteractiveMap';
import { DropzoneButton } from './DropzoneButton'; 

const RemoveFieldConfirmationModal = ({ fieldName, onConfirm, onCancel, opened }) => {
  return (
    <Modal
      title={`Remove Field "${fieldName}"`}
      size="sm"
      opened={opened}
      onClose={onCancel}
      shadow="xs"
    >
      <Text style={{ margin:'15px' }}>
        <FormattedMessage id="myfarm.text1" />{fieldName}<FormattedMessage id="myfarm.text2" />
      </Text>
      <Group position="right" spacing="md" style={{ marginTop: '25px', padding: '0 15px 15px' }}>
        <Button variant="light" color="blue" onClick={onCancel} style={{ marginRight: '10px' }}>
          <FormattedMessage id="button.cancel" />
        </Button>
        <Button variant="filled" color="red" onClick={onConfirm}>
          <FormattedMessage id="button.confirm" />
        </Button>
      </Group>
    </Modal>
  );
};

const GeoJSONModal = ({ 
  opened, 
  onClose, 
  geoJsonParams, 
  setGeoJsonParams, 
  generateGeoJSON, 
  showToast 
}) => {
  const intl = useIntl();

  const handleGenerate = async () => {
    if (geoJsonParams.company.trim() && geoJsonParams.country.trim()) {
      try {
        await generateGeoJSON(geoJsonParams.company, geoJsonParams.country);
        onClose();
        setGeoJsonParams({ company: '', country: '' });
      } catch (error) {
        // Error is already handled in generateGeoJSON function
      }
    } else {
      showToast('error', intl.formatMessage({ id: "myfarm.geojson.validation.required" }));
    }
  };

  return (
    <Modal
      title={intl.formatMessage({ id: "myfarm.geojson.modal.title" })}
      opened={opened}
      onClose={onClose}
      size="sm"
    >
      <TextInput
        label={intl.formatMessage({ id: "myfarm.geojson.company.label" })}
        placeholder={intl.formatMessage({ id: "myfarm.geojson.company.placeholder" })}
        value={geoJsonParams.company}
        onChange={(e) => setGeoJsonParams(prev => ({...prev, company: e.target.value}))}
        mb="md"
        size="md"
        variant="unstyled"
        styles={{
          input: {
            border: '1px solid #ced4da',
            borderRadius: '4px',
            padding: '8px 12px'
          }
        }}
      />
      <TextInput
        label={intl.formatMessage({ id: "myfarm.geojson.country.label" })}
        placeholder={intl.formatMessage({ id: "myfarm.geojson.country.placeholder" })}
        value={geoJsonParams.country}
        onChange={(e) => setGeoJsonParams(prev => ({...prev, country: e.target.value}))}
        mb="md"
        size="md"
        variant="unstyled"
        styles={{
          input: {
            border: '1px solid #ced4da',
            borderRadius: '4px',
            padding: '8px 12px'
          }
        }}
      />
      <Group position="right" mt="lg">
        <Button 
          variant="outline" 
          onClick={onClose}
          size="md"
        >
          <FormattedMessage id="myfarm.geojson.button.cancel" />
        </Button>
        <Button 
          color="blue"
          onClick={handleGenerate}
          size="md"
          disabled={!geoJsonParams.company.trim() || !geoJsonParams.country.trim()}
        >
          <FormattedMessage id="myfarm.geojson.button.generate" />
        </Button>
      </Group>
    </Modal>
  );
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';

  try {
    // Parse the timestamp (handles ISO format and Date objects)
    const date = new Date(timestamp);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) return timestamp;
    
    // Format as: "Apr 16, 2025, 2:30 PM"
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    // If parsing fails, return the original timestamp
    return timestamp;
  }
};

const CoordinateEditorModal = ({ opened, onClose, fieldData, onSave, onSelectCoordinateFromMap, onPointAdded, accountId }) => {
  const [editedPoints, setEditedPoints] = useState([]);
  const [selectedPointIndex, setSelectedPointIndex] = useState(null);

  const [editingField, setEditingField] = useState(null); // 'name', 'product', 'region', or null
  const [tempValue, setTempValue] = useState('');
  const [localFieldData, setLocalFieldData] = useState(null); 
  const [hasEudrReport, setHasEudrReport] = useState(false);
  const intl = useIntl();

  // Update localFieldData when fieldData prop changes
  useEffect(() => {
    setLocalFieldData(fieldData);
  }, [fieldData]);

  // Check for EUDR report when modal opens
  useEffect(() => {
    const checkEudrReport = async () => {
      if (!fieldData || !fieldData.name) {
        setHasEudrReport(false);
        return;
      }
      try {
        const username = accountId;
        if (!username) { setHasEudrReport(false); return; }
        const { exists } = await checkFieldHasReport(username, fieldData.name);
        setHasEudrReport(!!exists);
      } catch (error) {
        setHasEudrReport(false);
      }
    };

    if (opened) {
      checkEudrReport();
    }
  }, [opened, fieldData]);

    useEffect(() => {
    // This will be called when parent updates the field data with new coordinates
    if (fieldData && fieldData.gpsPoints) {
      setEditedPoints([...fieldData.gpsPoints]);
      setSelectedPointIndex(null); // Clear selection when coordinates are updated
    }
  }, [fieldData]);

  if (!fieldData || !opened || !localFieldData) return null;

  const handlePointChange = (pointIndex, field, value) => {
    const newPoints = [...editedPoints];
    newPoints[pointIndex] = {
      ...newPoints[pointIndex],
      [field]: parseFloat(value) || 0
    };
    setEditedPoints(newPoints);
  };

const addNewPoint = () => {
  // CRITICAL: Set prevention flag BEFORE any state changes
  window.preventMapReset = true;
  
  let newPoint;
  
  if (editedPoints.length > 0) {
    const lastPoint = editedPoints[editedPoints.length - 1];
    newPoint = {
      latitude: lastPoint.latitude,
      longitude: lastPoint.longitude,
      timestamp: new Date().toISOString()
    };
  } else {
    newPoint = {
      latitude: 0,
      longitude: 0,
      timestamp: new Date().toISOString()
    };
  }
  
  const updatedPoints = [...editedPoints, newPoint];
  setEditedPoints(updatedPoints);

  // if (fieldData && onSave) {
  //   // Call onSave to update the main dataset immediately
  //   // This will trigger the map to re-render with the new point
  //   onSave(updatedPoints);
  // }
  
  // Keep the prevention flag active for a longer duration
  // This ensures it stays active when user clicks the coordinate selection icon
  setTimeout(() => {
    // Only clear if we're not in coordinate selection mode
    if (!window.coordinateSelectionActive) {
      window.preventMapReset = true;
    }
  }, 2000); // Increased timeout
};

  const removePoint = (pointIndex) => {
    if (editedPoints.length <= 1) {
      return;
    }
    const newPoints = editedPoints.filter((_, index) => index !== pointIndex);
    setEditedPoints(newPoints);

    if (onPointAdded) {
      onPointAdded(newPoints);
    }

  };

  const handleSave = () => {
    // Combine GPS points with field metadata
    const updatedFieldData = {
      ...localFieldData,
      gpsPoints: editedPoints
    };
    
    onSave(editedPoints, updatedFieldData); // Pass both GPS points and complete field data
    window.coordinateEditorActive = false; 
    onClose();
  };

  const handleSelectPointOnMap = (pointIndex) => {

    setSelectedPointIndex(pointIndex);
    // Call the parent function to start coordinate selection
    if (onSelectCoordinateFromMap) {
      onSelectCoordinateFromMap(pointIndex, fieldData.name);
    }
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setTempValue(currentValue || '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleFieldSave();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleFieldSave = () => {
    if (tempValue.trim() === '') {
      // Revert to previous value if empty
      cancelEditing();
      return;
    }
    
    // Update local state immediately
    setLocalFieldData(prev => ({
      ...prev,
      [editingField]: tempValue.trim()
    }));
    
    setEditingField(null);
    setTempValue('');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleOpenEudrReport = async () => {
    try {
      const username = accountId;
      if (!username) return;
      const result = await checkFieldHasReport(username, fieldData.name);
      if (result?.exists && result?.documentUrl) {
        window.open(result.documentUrl, '_blank');
      }
    } catch (error) {
    }
  };

  return (
    <Paper 
      withBorder 
      style={{ 
        position: 'fixed',
        bottom: '60px',
        right: '20px',
        width: '600px',
        maxHeight: '60vh',
        zIndex: 1000,
        backgroundColor: 'white'
      }}
    >
      <Accordion defaultValue="coordinates">
        <Accordion.Item value="coordinates">
<Accordion.Control>
  <div style={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
    {/* Name Field */}
    <Text size="xs" color="dimmed" style={{ margin: '0' }}>
      <FormattedMessage id="myfarm.field.name" />:
    </Text>
    {editingField === 'name' ? (
      <TextInput
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleFieldSave}
        onKeyDown={handleKeyDown}
        size="sm"
        style={{ minWidth: '60px', margin: '0' }}
        autoFocus
      />
    ) : (
      <Text 
        style={{ cursor: 'pointer', textDecoration: 'underline dotted', opacity: 0.8, margin: '2px' }}
        onClick={() => startEditing('name', localFieldData.name)}
      >
        {localFieldData.name}
      </Text>
    )}
    
    {/* EUDR PDF Icon */}
    {hasEudrReport && (
      <ActionIcon 
        color="red" 
        variant="subtle"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenEudrReport();
        }}
        style={{ marginLeft: '8px' }}
        title={intl.formatMessage({ id: 'myfarm.tooltip.eudr' })}
      >
        <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '16px' }} />
      </ActionIcon>
    )}
    
    <span style={{ color: '#868e96', margin: '2px' }}>|</span>
    
    {/* Product Field */}
    <Text size="xs" color="dimmed" style={{ margin: '0' }}>
      <FormattedMessage id="myfarm.field.product" />:
    </Text>
    {editingField === 'product' ? (
      <TextInput
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleFieldSave}
        onKeyDown={handleKeyDown}
        size="sm"
        style={{ minWidth: '60px', margin: '0' }}
        autoFocus
      />
    ) : (
      <Text 
        style={{ cursor: 'pointer', textDecoration: 'underline dotted', opacity: 0.8, margin: '2px' }}
        onClick={() => startEditing('product', localFieldData.product)}
      >
        {localFieldData.product}
      </Text>
    )}
    
    <span style={{ color: '#868e96', margin: '2px' }}>|</span>
    
    {/* Region Field */}
    <Text size="xs" color="dimmed" style={{ margin: '0' }}>
      <FormattedMessage id="myfarm.field.region" />:
    </Text>
    {editingField === 'region' ? (
      <TextInput
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleFieldSave}
        onKeyDown={handleKeyDown}
        size="sm"
        style={{ minWidth: '60px', margin: '0' }}
        autoFocus
      />
    ) : (
      <Text 
        style={{ cursor: 'pointer', textDecoration: 'underline dotted', opacity: 0.8, margin: '2px' }}
        onClick={() => startEditing('region', localFieldData.region)}
      >
        {localFieldData.region}
      </Text>
    )}
  </div>
</Accordion.Control>
          <Accordion.Panel>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              <Table striped>
                <thead>
                  <tr>
                    <th>#</th>
                    <th><FormattedMessage id="myfarm.field.latitude" /></th>
                    <th><FormattedMessage id="myfarm.field.longitude" /></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {editedPoints.map((point, index) => (
                    <tr key={index} style={{ 
                      backgroundColor: selectedPointIndex === index ? '#e3f2fd' : 'transparent' 
                    }}>
                      <td>{index + 1}</td>
                      <td>
                        <TextInput
                          value={point.latitude}
                          onChange={(e) => handlePointChange(index, 'latitude', e.target.value)}
                          size="sm"
                        />
                      </td>
                      <td>
                        <TextInput
                          value={point.longitude}
                          onChange={(e) => handlePointChange(index, 'longitude', e.target.value)}
                          size="sm"
                        />
                      </td>
                      <td>
                        <Group spacing={4}>
                          <ActionIcon 
                            color="blue"
                            variant={selectedPointIndex === index ? 'filled' : 'light'}
                            onClick={() => handleSelectPointOnMap(index)}
                            size="sm"
                            title={intl.formatMessage({ id: "map.coordinate.select.title" })}
                          >
                            <IconTarget size={14} />
                          </ActionIcon>
                          <ActionIcon 
                            color="red" 
                            onClick={() => removePoint(index)}
                            disabled={editedPoints.length <= 1}
                            size="sm"
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            
            {selectedPointIndex !== null && (
              <div style={{ 
                padding: '1px 1px',        // Reduce vertical padding
                backgroundColor: '#e3f2fd', 
                borderRadius: '4px', 
                margin: '0px',
                display: 'flex',            // Use flex to control height
                alignItems: 'center',       // Center content vertically
                minHeight: 'auto',
                height: 'fit-content',     // Only take space needed
                maxHeight: '30px',         // Limit maximum height
                display: 'block'           // Ensure block display

              }}>
                <Text size="sm" color="blue">
                  <FormattedMessage id="map.coordinate.update" /> {selectedPointIndex + 1}
                  <ActionIcon 
                    size="sm" 
                    variant="subtle" 
                    onClick={() => setSelectedPointIndex(null)}
                    style={{ marginLeft: '0px' }}
                  >
                    <IconX size={12} />
                  </ActionIcon>
                </Text>
              </div>
            )}
            
            {/* MOVED: Buttons are now INSIDE the Accordion.Panel */}
            <Group position="apart" mt="md" style={{ padding: '10px 0' }}>
              <Button 
                variant="outline" 
                onClick={addNewPoint} 
                leftIcon={<IconMapPin size={16} />}
                size="sm"
              >
                <FormattedMessage id="myfarm.button.add" />
              </Button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button variant="outline" onClick={onClose}>
                  <FormattedMessage id="button.cancel" />
                </Button>
                <Button 
                  color="teal" 
                  onClick={handleSave}
                  disabled={editedPoints.length < 1}
                >
                  <FormattedMessage id="button.save" />
                </Button>
              </div>
            </Group>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Paper>
  );
};

export default function MyFarm({ accountIdOverride = null }) {
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));
  const activeAccountId = accountIdOverride || userinfo?.accountId;
  const userFullDetails = useSelector((state) => state.user.userFullDetails);
  const dispatch = useDispatch();
  const [lastClickTime, setLastClickTime] = useState(0);

  const [farmFields, setFarmFields] = useState([]);
  const [isLoadingGPS, setIsLoadingGPS] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [gmapsEntry, setGmapsEntry] = useState(false);
  const [gmapsLink, setGmapsLink] = useState('');
  const [isResolvingLink, setIsResolvingLink] = useState(false);

  const parseGoogleMapsLink = (url) => {
    // Pattern 1: standard @lat,lng (e.g. /maps/@3.1234,5.6789,17z)
    let match = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) return { latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) };
    // Pattern 2: ?q=lat,lng or &q=lat,lng
    match = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) return { latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) };
    // Pattern 3: /place/lat,lng/ — produced by goo.gl short-link redirects
    match = url.match(/\/place\/(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) return { latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) };
    // Pattern 4: !3d{lat}!4d{lng} — Google Maps data encoding
    const lat3d = url.match(/!3d(-?\d+\.?\d*)/);
    const lng4d = url.match(/!4d(-?\d+\.?\d*)/);
    if (lat3d && lng4d) return { latitude: parseFloat(lat3d[1]), longitude: parseFloat(lng4d[1]) };
    // Pattern 5: ll=lat,lng
    match = url.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) return { latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) };
    return null;
  };

  const handleSaveGmapsLink = async (fieldIndex) => {
    let urlToParse = gmapsLink.trim();
    if (!urlToParse) {
      toast.error('Please paste a Google Maps link first', { ...ToastProperties });
      return;
    }
    setIsResolvingLink(true);
    // Resolve short links (maps.app.goo.gl etc.) via backend proxy
    if (urlToParse.includes('goo.gl')) {
      try {
        const res = await fetch(`${BASE_URL}/api/resolve-url?url=${encodeURIComponent(urlToParse)}`);
        const data = await res.json();
        if (data.resolvedUrl) urlToParse = data.resolvedUrl;
      } catch {
        setIsResolvingLink(false);
        toast.error('Could not resolve the short link. Please use the full Google Maps URL.', { ...ToastProperties });
        return;
      }
    }
    const parsed = parseGoogleMapsLink(urlToParse);
    setIsResolvingLink(false);
    if (!parsed) {
      toast.error('Could not extract coordinates from this Google Maps link', { ...ToastProperties });
      return;
    }
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].gpsPoints.push({ ...parsed, timestamp: new Date() });
    setFarmFields(updatedFields);
    setGmapsLink('');
    toast.success(`Coordinates extracted: ${parsed.latitude}, ${parsed.longitude}`, { ...ToastProperties });
  };
  const [userDocuments, setUserDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFields, setExpandedFields] = useState({});
  const [fieldData, setFieldData] = useState([]);
  const [mapHeight, setMapHeight] = useState(300);
  const [isDragging, setIsDragging] = useState(false);
  const [geoJsonModalOpen, setGeoJsonModalOpen] = useState(false);
  const [geoJsonParams, setGeoJsonParams] = useState({ company: '', country: '' });
  const [selectedPointForMap, setSelectedPointForMap] = useState(null);
  const [isLoadingCertificates, setIsLoadingCertificates] = useState(false);
  const certificatesLoadedRef = useRef(false);
  // Use refs instead of state to avoid re-renders that interfere with Leaflet
  const fieldCertificatesRef = useRef({});
  const fieldQRCodesRef = useRef({});
  const fieldEudrReportsRef = useRef({});
  const [generatingEudrForField, setGeneratingEudrForField] = useState({});
  const [, forceUpdate] = useState({});
  const isAddingFieldRef = useRef(false);
  const savedMapViewRef = useRef(null);
  const loadCertificatesAbortController = useRef(null); // To cancel background loading
  
  // Use useLayoutEffect to restore map view IMMEDIATELY after render, before browser paint
  useLayoutEffect(() => {
    if (savedMapViewRef.current && mapComponentRef.current?.mapInstanceRef?.current) {
      const map = mapComponentRef.current.mapInstanceRef.current;
      const { zoom, center } = savedMapViewRef.current;
      map.setView(center, zoom, { animate: false });
      savedMapViewRef.current = null; // Clear after restoring
    }
  });

  useEffect(() => {
    if (accountIdOverride) {
      getFieldAPI({ accountId: accountIdOverride })
        .then((data) => {
          if (Array.isArray(data.result)) {
            setFieldData(data.result);
            const expanded = {};
            data.result.forEach((_, index) => { expanded[index] = false; });
            setExpandedFields(expanded);
          } else {
            setFieldData([]);
          }
        })
        .catch(() => setFieldData([]));
    } else if (!userFullDetails.userId) {
      dispatch(getUserFullDetails({ email: userinfo.email }));
    } else {
      getFieldAPI(userFullDetails)
        .then((data) => {
          if (Array.isArray(data.result)) {
            setFieldData(data.result);
            // Initialize expanded state for all fields (expanded by default)
            const expanded = {};
            data.result.forEach((_, index) => {
              expanded[index] = false; // Set to true by default to show tables
            });
            setExpandedFields(expanded);
          } else {
            setFieldData([]);
          }
        })
        .catch(() => {});
    }
  }, [dispatch, userinfo?.email, userFullDetails.userId, accountIdOverride]);

  // Fetch user documents from blob storage
  useEffect(() => {
    if (userinfo?.accountId) {
      fetchUserDocuments();
    }
  }, [userinfo?.accountId]);

  // Load certificates and QR codes lazily when fields are displayed (not on mount)
  // This prevents interference with map initialization
  useEffect(() => {
    // Only load if fields are expanded and map has had time to initialize
    if (fieldData && fieldData.length > 0 && !isLoadingCertificates && !certificatesLoadedRef.current) {
      // Wait for user interaction or a long delay to ensure map stability
      const timer = setTimeout(() => {
        certificatesLoadedRef.current = true;
        loadFieldCertificatesAndQRCodes(fieldData);
      }, 500);
      
      return () => {
        clearTimeout(timer);
        // Cancel loading if component unmounts or dependencies change
        if (loadCertificatesAbortController.current) {
          loadCertificatesAbortController.current.abort();
        }
      };
    }
  }, [fieldData.length]); // Only trigger when the number of fields changes, not on every fieldData update
  
  const fetchUserDocuments = async () => {
    if (isLoadingDocuments || !userinfo?.accountId) return;
    setIsLoadingDocuments(true);
    try {
      // Get the authentication token from localStorage or wherever you store it
      const authToken = localStorage.getItem('authToken');
      
      // Call your API endpoint to list files from the blob storage
      const response = await fetch(`${BASE_URL}/api/documents/${userinfo.accountId}`, {
        method: 'GET',
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
          apikey: "abc123",
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }
      
      const documents = await response.json();
      
      // Map the API response to the format expected by your UI
      const processedDocuments = documents.map(doc => {
        // Determine document type based on file extension
        const extension = doc.name.split('.').pop().toLowerCase();
        let type = 'file';
        
        if (['pdf'].includes(extension)) {
          type = 'pdf';
        } else if (['xlsx', 'xls'].includes(extension)) {
          type = 'excel';
        } else if (['txt', 'doc', 'docx', 'geojson'].includes(extension)) {
          type = 'text';
        }
        
        return {
          name: doc.name,
          type: type,
          url: doc.url,
          lastModified: doc.lastModified,
          size: doc.size
        };
      });
      
      setUserDocuments(processedDocuments);
      setIsLoadingDocuments(false);
    } catch (error) {
      setIsLoadingDocuments(false);
      showToast('error', intl.formatMessage({ id: "myfarm.eudr.documents.error" }));
    }
  };

  const openDocument = (documentUrl) => {
    window.open(documentUrl, '_blank');
  };

  const getDocumentIcon = (docType) => {
    switch (docType) {
      case 'pdf':
        return <FontAwesomeIcon icon={faFilePdf} size="3x" style={{ color: "#E74C3C" }} />;
      case 'excel':
        return <FontAwesomeIcon icon={faFileExcel} size="3x" style={{ color: "#27AE60" }} />;
      case 'text':
        return <FontAwesomeIcon icon={faFileAlt} size="3x" style={{ color: "#3498DB" }} />;
      default:
        return <FontAwesomeIcon icon={faFile} size="3x" style={{ color: "#7F8C8D" }} />;
    }
  };

  // Format field name to match product ID format (lowercase, no spaces, ending with 1)
  const formatFieldNameToProductId = (fieldName) => {
    if (!fieldName) return '';
    return fieldName.toLowerCase().replace(/\s+/g, '') + '1';
  };

  // Check if certificate exists for a field
  const checkCertificateAvailability = async (fieldName) => {
    try {
      const productId = formatFieldNameToProductId(fieldName);
      const response = await fetch(`${BASE_URL}/certificateContent?product_id=${productId}`, { 
        method: 'HEAD',
        mode: 'cors'
      });
      return response.ok;
    } catch (error) {
      // Silently fail - certificate checking is not critical
      return false;
    }
  };

  // Check if QR code exists for a field
  const checkQRCodeAvailability = async (fieldName) => {
    try {
      const productId = formatFieldNameToProductId(fieldName);
      const qrCodeUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/qrcode/${productId}.png`;
      const response = await fetch(qrCodeUrl, { 
        method: 'HEAD',
        mode: 'cors'
      });
      return response.ok;
    } catch (error) {
      // Silently fail - QR code checking is not critical
      return false;
    }
  };

  // Check if EUDR report exists for a field
  const checkEudrReportAvailability = async (fieldName) => {
    try {
      const username = activeAccountId;
      if (!username) return false;

      const { exists } = await checkFieldHasReport(username, fieldName);
      return exists;
    } catch (error) {
      return false;
    }
  };

  // Generate EUDR report for a field
  const generateEudrReport = async (fieldName, fieldData) => {
    try {
      const username = activeAccountId;
      if (!username) {
        showToast('error', intl.formatMessage({ id: 'myfarm.eudr.noaccount' }));
        return;
      }

      // Guard: prevent duplicate generation even if the UI check was stale.
      // Check in-memory ref first (instant), then confirm with the backend.
      let reportAlreadyExists = !!fieldEudrReportsRef.current[fieldName];
      if (!reportAlreadyExists) {
        const { exists } = await checkFieldHasReport(username, fieldName).catch(() => ({ exists: false }));
        reportAlreadyExists = exists;
      }

      if (reportAlreadyExists) {
        // Sync the UI so the download icon appears
        fieldEudrReportsRef.current[fieldName] = true;
        forceUpdate({});
        showToast('info', intl.formatMessage({ id: 'myfarm.eudr.alreadyexists' }));
        return;
      }

      setGeneratingEudrForField(prev => ({ ...prev, [fieldName]: true }));
      showToast('info', intl.formatMessage({ id: 'myfarm.eudr.generating' }));

      // Convert gpsPoints to polygon format
      const polygon = fieldData.gpsPoints.map(point => ({
        lat: point.latitude,
        lon: point.longitude
      }));

      const params = {
        polygon,
        fieldName,
        accountId: username,
        zoom: 17,
        tileRadius: 2
      };

      const result = await dispatch(generateReport(params)).unwrap();
      
      showToast('success', intl.formatMessage({ id: 'myfarm.eudr.success' }));

      fieldEudrReportsRef.current[fieldName] = true;
      forceUpdate({});
      
    } catch (error) {
      showToast('error', intl.formatMessage({ id: 'myfarm.eudr.error' }));
    } finally {
      setGeneratingEudrForField(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Open EUDR report for a field
  const downloadEudrReport = async (fieldName) => {
    try {
      const username = activeAccountId;
      if (!username) {
        showToast('error', intl.formatMessage({ id: 'myfarm.eudr.noaccount' }));
        return;
      }
      
      // Folder uses uppercase with underscores, filename uses original case with underscores
      const fieldNameUpper = fieldName.toUpperCase().replace(/\s+/g, '_');
      const fieldNameFile = fieldName.replace(/\s+/g, '_');
      
      // Find the latest EUDR report (check last 30 days)
      const today = new Date();
      for (let i = 0; i < 30; i++) {
        const checkDate = new Date(today);
        checkDate.setDate(checkDate.getDate() - i);
        const dateStr = checkDate.toISOString().split('T')[0].replace(/-/g, '');
        const eudrUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/eudr/${username}/${fieldNameUpper}/${dateStr}_VertoEUDRDueDiligence_${fieldNameFile}.pdf`;
        
        try {
          // Add timeout to prevent hanging requests
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
          
          const response = await fetch(eudrUrl, { 
            method: 'HEAD',
            signal: controller.signal 
          });
          clearTimeout(timeoutId);
          
          if (response.ok) {
            window.open(eudrUrl, '_blank');
            showToast('success', intl.formatMessage({ id: 'myfarm.eudr.opened' }));
            return;
          }
        } catch (err) {
          // Continue checking other dates
          continue;
        }
      }
      
      showToast('error', intl.formatMessage({ id: 'myfarm.eudr.notfound' }));
    } catch (error) {
      showToast('error', intl.formatMessage({ id: 'myfarm.eudr.error' }));
    }
  };

  // Load certificate and QR code availability for all fields
  const loadFieldCertificatesAndQRCodes = async (fields) => {
    if (!Array.isArray(fields) || fields.length === 0 || isLoadingCertificates) return;
    
    // Cancel any previous loading
    if (loadCertificatesAbortController.current) {
      loadCertificatesAbortController.current.abort();
    }
    
    // Create new abort controller for this loading session
    loadCertificatesAbortController.current = new AbortController();
    const { signal } = loadCertificatesAbortController.current;
    
    setIsLoadingCertificates(true);
    
    try {
      const certificatePromises = fields.map(async (field) => {
        const fieldName = field.name || Object.keys(field)[0];
        try {
          if (signal.aborted) return { fieldName, hasCertificate: false };
          const hasCertificate = await checkCertificateAvailability(fieldName);
          return { fieldName, hasCertificate };
        } catch (error) {
          return { fieldName, hasCertificate: false };
        }
      });
      
      const qrCodePromises = fields.map(async (field) => {
        const fieldName = field.name || Object.keys(field)[0];
        try {
          if (signal.aborted) return { fieldName, hasQRCode: false };
          const hasQRCode = await checkQRCodeAvailability(fieldName);
          return { fieldName, hasQRCode };
        } catch (error) {
          return { fieldName, hasQRCode: false };
        }
      });
      
      const eudrPromises = fields.map(async (field) => {
        const fieldName = field.name || Object.keys(field)[0];
        try {
          if (signal.aborted) return { fieldName, hasEudrReport: false };
          const hasEudrReport = await checkEudrReportAvailability(fieldName);
          return { fieldName, hasEudrReport };
        } catch (error) {
          return { fieldName, hasEudrReport: false };
        }
      });
      
      const [loadedCertificates, loadedQRCodes, loadedEudrReports] = await Promise.all([
        Promise.all(certificatePromises),
        Promise.all(qrCodePromises),
        Promise.all(eudrPromises)
      ]);
      
      // Don't update state if aborted
      if (signal.aborted) {
        setIsLoadingCertificates(false);
        return;
      }
      
      const certificatesMap = {};
      const qrCodesMap = {};
      const eudrReportsMap = {};
      
      loadedCertificates.forEach(item => {
        certificatesMap[item.fieldName] = item.hasCertificate;
      });
      
      loadedQRCodes.forEach(item => {
        qrCodesMap[item.fieldName] = item.hasQRCode;
      });
      
      loadedEudrReports.forEach(item => {
        eudrReportsMap[item.fieldName] = item.hasEudrReport;
      });
      
      // Use refs to avoid triggering re-renders that break Leaflet
      fieldCertificatesRef.current = certificatesMap;
      fieldQRCodesRef.current = qrCodesMap;
      fieldEudrReportsRef.current = eudrReportsMap;
      setIsLoadingCertificates(false);
      
      // Force a single re-render after data is loaded
      forceUpdate({});
    } catch (error) {
      setIsLoadingCertificates(false);
    }
  };

  // Download certificate for a field
  const downloadCertificate = async (fieldName) => {
    try {
      const productId = formatFieldNameToProductId(fieldName);
      const certificateUrl = `${BASE_URL}/certificateContent?product_id=${productId}`;
      window.open(certificateUrl, '_blank');
      showToast('success', intl.formatMessage({ id: 'myfarm.certificate.opened' }));
    } catch (error) {
      showToast('error', intl.formatMessage({ id: 'myfarm.certificate.error' }));
    }
  };

  // Open QR code image for a field
  const downloadQRCode = async (fieldName) => {
    try {
      const productId = formatFieldNameToProductId(fieldName);
      const qrCodeUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/qrcode/${productId}.png`;
      
      // Open QR code image in a new tab
      window.open(qrCodeUrl, '_blank');
      
      showToast('success', intl.formatMessage({ id: 'myfarm.qrcode.opened' }));
    } catch (error) {
      showToast('error', intl.formatMessage({ id: 'myfarm.qrcode.error' }));
    }
  };

  const [showFieldSection, setShowFieldSection] = useState(false);
  
  const handleAddField = () => {
    // SAVE current map zoom and center in ref (persists across renders)
    if (mapComponentRef.current?.mapInstanceRef?.current) {
      const map = mapComponentRef.current.mapInstanceRef.current;
      savedMapViewRef.current = {
        zoom: map.getZoom(),
        center: map.getCenter()
      };
    }
    
    // CRITICAL: Set ALL prevention flags IMMEDIATELY before any state changes
    isAddingFieldRef.current = true;
    window.preventMapReset = true;
    window.coordinateSelectionActive = true;
    window.lastMapInteraction = Date.now();
    window.mapHasBeenInitialized = true;
    
    // State changes happen synchronously
    // Only add a new field if the field section is not already shown
    if (!showFieldSection && farmFields.length === 0) {
      const field = { name: '', gpsPoints: [], manualLat: '', manualLng: '', region: "", product: "" };
      setGmapsLink('');
      setGmapsEntry(false);
      setManualEntry(false);
      setFarmFields([...farmFields, field]);
    }
    // Toggle the showFieldSection state regardless of whether a field was added or not
    setShowFieldSection(!showFieldSection);
    
    // Reset the flags after all renders complete
    setTimeout(() => {
      window.preventMapReset = false;
      window.coordinateSelectionActive = false;
      isAddingFieldRef.current = false;
    }, 3000);
  };

  const handleCollectGPS = async (fieldIndex) => {
    const currentTime = Date.now();
    if (currentTime - lastClickTime < 2000) {
      // If the last click was less than 2 seconds ago, ignore this click
      return;
    }

    setLastClickTime(currentTime);

    setIsLoadingGPS(true);
    navigator.geolocation.getCurrentPosition(function () {}, function () {}, {});
    await navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const updatedFields = [...farmFields];
        updatedFields[fieldIndex].gpsPoints.push({ latitude, longitude, timestamp: new Date() });
        setFarmFields(updatedFields);
        setIsLoadingGPS(false);
      },
      () => {
        setIsLoadingGPS(false);
      },{timeout:10000, enableHighAccuracy: true, maximumAge: 0}
    );
  };

  const handleFieldNameChange = (e, fieldIndex) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].name = e.target.value;
    setFarmFields(updatedFields);
  };

  const handleManualLatChange = (e, fieldIndex) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].manualLat = e.target.value;
    setFarmFields(updatedFields);
  };

  const handleManualLngChange = (e, fieldIndex) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].manualLng = e.target.value;
    setFarmFields(updatedFields);
  };

  const handleSaveManualGPS = (fieldIndex) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].gpsPoints.push({
      latitude: parseFloat(updatedFields[fieldIndex].manualLat),
      longitude: parseFloat(updatedFields[fieldIndex].manualLng),
      timestamp: new Date() 
    });
    setFarmFields(updatedFields);
  };

  const isSaveButtonDisabled = farmFields.some((field) => field.gpsPoints.length < 1);

  const handleSave = (farmFieldsToSave) => {
    if (farmFieldsToSave.length === 0) {
      showToast('error', intl.formatMessage({ id: "myfarm.no.fields.to.save" }));
      return;
    }

    const hasInvalidFieldName = farmFieldsToSave.some(
      (field) => !field?.name || !field.name.trim()
    );

    if (hasInvalidFieldName) {
      showToast('error', intl.formatMessage({ id: "myfarm.field.name" }) + ' is required');
      return;
    }

    const hasInvalidGpsPoints = farmFieldsToSave.some(
      (field) => !Array.isArray(field.gpsPoints) || field.gpsPoints.length < 1
    );

    if (hasInvalidGpsPoints) {
      showToast('error', intl.formatMessage({ id: "myfarm.need.one.point", defaultMessage: 'At least 1 GPS point is required' }));
      return;
    }

    // SAVE current map view before saving fields
    if (mapComponentRef.current?.mapInstanceRef?.current) {
      const map = mapComponentRef.current.mapInstanceRef.current;
      savedMapViewRef.current = {
        zoom: map.getZoom(),
        center: map.getCenter()
      };
    }

    // Set prevention flags
    window.preventMapReset = true;
    window.coordinateSelectionActive = true;
    window.lastMapInteraction = Date.now();

    const dataToSave = {
      userId: userinfo?.userId,
      userAccountId: activeAccountId,
      farmFields: [...fieldData, ...farmFieldsToSave],
    };

    addFieldAPI(dataToSave)
      .then((response) => {
        if (response.status === "Success") {
          showToast('success', 'Field saved successfully');  
          setFarmFields([]);
          setShowFieldSection(false);
          // After saving, fetch and update the field data
          getFieldAPI({ accountId: activeAccountId })
            .then((data) => {
              if (Array.isArray(data.result)) {
                setFieldData(data.result);
                // Update expanded state for new fields
                const expanded = {...expandedFields};
                data.result.forEach((_, index) => {
                  if (expanded[index] === undefined) {
                    expanded[index] = true; // Set new fields to be expanded by default
                  }
                });
                setExpandedFields(expanded);
                
                // Reset prevention flags after data is updated
                setTimeout(() => {
                  window.preventMapReset = false;
                  window.coordinateSelectionActive = false;
                }, 2000);
              } else {
                console.error('Invalid data format:', data);
                setFieldData([]);
                window.preventMapReset = false;
                window.coordinateSelectionActive = false;
              }
            })
            .catch((error) => {
              console.error('Error fetching data:', error);
              window.preventMapReset = false;
              window.coordinateSelectionActive = false;
            });
        } else {
          showToast('error', 'Failed to save data');   
        }
      })
      .catch((error) => {
        console.error('Error saving data:', error);
        showToast('error', 'An error occurred while saving data');           
      });
  };
  
  // Initialize editedFields with the existing fieldData
  const [editedFields, setEditedFields] = useState([]);

  const [isEditing, setIsEditing] = useState(false); // State to track editing mode
  const [editingField, setEditingField] = useState(null);

  const handleEditField = (fieldName) => {
    setEditingField(fieldName);
    setIsEditing(true);
  
    // Clone the original farmFields for editing
    const clonedFields = JSON.parse(JSON.stringify(fieldData));
    setEditedFields(clonedFields);
  };  

  const handleSaveField = (fieldName) => {
    setIsEditing(false);

    const editedField = editedFields[fieldName];
    const originalField = fieldData[fieldName];

    // Check if any field is modified
    const isNameModified = editedField.name !== originalField.name;
    const isProductModified = editedField.product !== originalField.product;
    const isRegionModified = editedField.region !== originalField.region;
    const isGpsPointsModified = !arraysAreEqual(editedField.gpsPoints, originalField.gpsPoints);

    // Save if any field is modified
    if (isNameModified || isProductModified || isRegionModified || isGpsPointsModified) {
      const dataToSave = {
        userId: userinfo?.userId,
        userAccountId: activeAccountId,
        farmFields: editedFields,
      };
      addFieldAPI(dataToSave)
        .then((response) => {
          if (response.status === "Success") {
            showToast('success', 'Field updated successfully');
            // After saving, fetch and update the field data
            getFieldAPI({ accountId: activeAccountId })
              .then((data) => {
                if (Array.isArray(data.result)) {
                  setFieldData(data.result);
                } else {
                  console.error('Invalid data format:', data);
                  setFieldData([]);
                }
              })
              .catch((error) => {
                console.error('Error fetching data:', error);
              });
          } else {
            showToast('error', 'Failed to update field');
          }
        })
        .catch((error) => {
          console.error('Error updating field:', error);
          showToast('error', 'An error occurred while saving data');
        });
    }

    // Update the original farmFields with edited data
    setFieldData(editedFields);
  };

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [fieldToRemove, setFieldToRemove] = useState(null);

  const handleRemoveField = (index) => {
    const fieldToRemove = fieldData[index].name;
    // Open the confirmation modal
    setIsConfirmationModalOpen(true);
    setFieldToRemove(fieldToRemove);
  };

  const handleConfirmRemove = () => {
    // Close the confirmation modal when confirmed
    setIsConfirmationModalOpen(false);

    // Your removal logic here
    const updatedEditedFields = fieldData.filter((field) => field.name !== fieldToRemove);
    setEditedFields(updatedEditedFields);

    // Upload the updated data without the removed field
    const dataToSave = {
      userId: userinfo?.userId,
      userAccountId: activeAccountId,
      farmFields: updatedEditedFields,
    };

  addFieldAPI(dataToSave)
    .then((response) => {
      if (response.status === "Success") {
        showToast('success', 'Field removed successfully');        
        // After removing the field, fetch and update the field data
        getFieldAPI({ accountId: activeAccountId })
          .then((data) => {
            if (Array.isArray(data.result)) {
              setFieldData(data.result);
              // Update expanded state for remaining fields
              const expanded = {};
              data.result.forEach((_, index) => {
                expanded[index] = expandedFields[index] || true; // Keep expanded state, default to true
              });
              setExpandedFields(expanded);
            } else {
              console.error('Invalid data format:', data);
              setFieldData([]);
            }
          })
          .catch((error) => {
            console.error('Error fetching data:', error);
          });
      } else {
        showToast('error', 'Failed to remove field');        
      }
    })
    .catch((error) => {
      console.error('Error removing field:', error);
      showToast('error', 'An error occurred while removing the field');  
    });
  };

  const handleCancelRemove = () => {
    // Your cancel logic here
    // Close the confirmation modal when canceled
    setIsConfirmationModalOpen(false);
  };
  
  // Helper function to check if two arrays are equal
  function arraysAreEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
      return false;
    }
    
    for (let i = 0; i < arr1.length; i++) {
      if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
        return false;
      }
    }
    
    return true;
  }  
  
  const handleEditValues = (e, fieldName, rowIndex, columnName) => {
    if (isEditing) {
      const updatedFields = [...editedFields];
      const fieldIndex = parseInt(fieldName, 10); // base 10
      updatedFields[fieldIndex]["gpsPoints"][rowIndex][columnName] = e.target.value;
      setEditedFields(updatedFields);
    }
  };

  const handleEditName = (e, fieldName, key) => {
    if (isEditing) {
      const updatedFields = [...editedFields];
      const fieldIndex = parseInt(fieldName, 10); // base 10
      updatedFields[fieldIndex][key] = e.target.value;
      setEditedFields(updatedFields);
    }
  };

  const handleFieldChange = (e, fieldIndex, key) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex][key] = e.target.value;
    setFarmFields(updatedFields);
  };  

  const showToast = (type, message) => {
    // Use the toast function to display the appropriate toast message
    if (type === 'success') {
      toast.success(message, {
        ...ToastProperties
      });
    } else if (type === 'error') {
      toast.error(message, {
        ...ToastProperties
      });
    }
  };

  const intl = useIntl();

  const mapurl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/farm_fields_map.html`;

  // Function to toggle field expansion
  const toggleFieldExpansion = (index) => {
    // Save current scroll position
    const scrollPosition = window.scrollY;
    
    // Toggle the field
    setExpandedFields({
      ...expandedFields,
      [index]: !expandedFields[index]
    });
    
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  // Filter fields based on search term
  const filteredFields = Object.keys(fieldData).filter(fieldName => {
    const field = fieldData[fieldName];
    return (
      field?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field?.product?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field?.region?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const formatFieldDate = (field) => {
    const time = getFieldTimestamp(field);
    if (!time) return UNDATED_LABEL;
    return intl.formatDate(time, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  // Which year sections are open. A year absent from this map falls back to the
  // default below, so the user's explicit toggles always win.
  const [openYears, setOpenYears] = useState({});

  // Groups the fieldData indexes - not the fields themselves - so the indexes the
  // edit, delete and map handlers rely on keep pointing at the same entries.
  const fieldGroups = groupByYear(filteredFields, (key) => getFieldTimestamp(fieldData[key]));

  // Newest year open by default, older years collapsed: the fields a user just
  // uploaded are the ones they are looking for.
  const newestYear = fieldGroups[0]?.year;
  const isYearOpen = (year) => openYears[String(year)] ?? year === newestYear;
  const toggleYear = (year) => {
    setOpenYears((prev) => ({ ...prev, [String(year)]: !(prev[String(year)] ?? year === newestYear) }));
  };

  // Flattened into year headers followed by their (visible) fields, so the whole
  // thing stays one table and the columns line up across sections.
  const tableRows = [];
  fieldGroups.forEach((group) => {
    tableRows.push({ type: 'year', group });
    if (isYearOpen(group.year)) {
      group.items.forEach((fieldName) => tableRows.push({ type: 'field', fieldName }));
    }
  });

  const generateGeoJSON = async (company = "", country = "") => {
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      showToast('info', intl.formatMessage({ id: "myfarm.geojson.toast.generating" }));
      
      const response = await fetch(`${BASE_URL}/api/generate-geojson/${userinfo.accountId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          apikey: 'abc123'
        },
        body: JSON.stringify({ company, country })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Show success message with feature count
      showToast('success', intl.formatMessage({ id: "myfarm.geojson.toast.success" }, { features: result.features || 0 }));
      
      // Optionally open the GeoJSON file if URL is provided
      if (result.url) {
        window.open(result.url, '_blank');
      }
      
      return result;
      
    } catch (error) {
      console.error('Error generating GeoJSON:', error);
      
      // Provide more specific error messages based on error type
      let errorMessage = intl.formatMessage({ id: "myfarm.geojson.toast.error" });
      
      if (error.message.includes('401')) {
        errorMessage = 'Security error: Invalid token. You need to logout and login again in order to operate';
      } else if (error.message.includes('403')) {
        errorMessage = 'Access denied. Please check your permissions.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Service not found. Please contact support.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      showToast('error', errorMessage);
      throw error;
    }
  };

  const [focusedFieldIndex, setFocusedFieldIndex] = useState(null);
const handleFocusField = (fieldIndex) => {
  // Convert string index to number
  const numericIndex = parseInt(fieldIndex, 10);
  
  // Clear prevention flags when user explicitly requests focus
  window.preventMapReset = false;
  window.coordinateSelectionActive = false;
  
  setFocusedFieldIndex(numericIndex);
};

  const [coordinateEditorOpen, setCoordinateEditorOpen] = useState(false);
  const [editingCoordinateField, setEditingCoordinateField] = useState(null);

  const [coordinateSelectionMode, setCoordinateSelectionMode] = useState(false);
  const [selectionPointInfo, setSelectionPointInfo] = useState(null);

  const [savedZoom, setSavedZoom] = useState(null);
  const [savedCenter, setSavedCenter] = useState(null);
  const mapComponentRef = useRef(null);

  // Updated handleSelectCoordinateFromMap function
  const handleSelectCoordinateFromMap = (pointIndex, fieldName) => {
    
    // Set both prevention flags
    window.preventMapReset = true;
    window.coordinateSelectionActive = true;
    
    setSelectionPointInfo({
      pointIndex,
      fieldName
    });
    setCoordinateSelectionMode(true);
  };

  const handleCoordinateSelect = (lat, lng) => {
    
    if (lat === null || lng === null) {
      // Cancel selection - clear both flags
      window.preventMapReset = false;
      window.coordinateSelectionActive = false;
      setCoordinateSelectionMode(false);
      setSelectionPointInfo(null);
      return;
    }

    if (selectionPointInfo && editingCoordinateField) {
      // DEBUGGING: Capture old vs new coordinates

      const pointIndex = selectionPointInfo.pointIndex;
      const oldCoordinates = editingCoordinateField.gpsPoints?.[pointIndex] ? {
        latitude: editingCoordinateField.gpsPoints[pointIndex].latitude,
        longitude: editingCoordinateField.gpsPoints[pointIndex].longitude
      } : { latitude: 'undefined', longitude: 'undefined' };
      
      const newCoordinates = {
        latitude: lat,
        longitude: lng
      };
      
      // CREATE DEBUG VARIABLES FOR BREAKPOINT
      const coordinateComparison = {
        pointIndex: pointIndex,
        fieldName: editingCoordinateField.name,
        old: oldCoordinates,
        new: newCoordinates,
        changed: oldCoordinates.latitude !== lat || oldCoordinates.longitude !== lng
      };
      
      // Create a copy of the field to update
      const updatedField = { ...editingCoordinateField };
      
      // Ensure gpsPoints array exists
      if (!updatedField.gpsPoints) {
        updatedField.gpsPoints = [];
      }
      
      // Extend the array if the index doesn't exist yet
      while (updatedField.gpsPoints.length <= selectionPointInfo.pointIndex) {
        updatedField.gpsPoints.push({
          latitude: 0,
          longitude: 0,
          timestamp: new Date().toISOString()
        });
      }
      
      // Update the point
      updatedField.gpsPoints[selectionPointInfo.pointIndex] = {
        ...updatedField.gpsPoints[selectionPointInfo.pointIndex],
        latitude: lat,
        longitude: lng,
        timestamp: new Date().toISOString()
      };
      
      // Update the editingCoordinateField state
      setEditingCoordinateField(updatedField);
      
      // Update the main fieldData to refresh the map
      const fieldIndex = fieldData.findIndex(field => field.name === editingCoordinateField.name);
      if (fieldIndex !== -1) {
        const updatedFieldData = [...fieldData];
        updatedFieldData[fieldIndex] = updatedField;
        
        // KEEP FLAGS SET WHILE UPDATE HAPPENS
        window.preventMapReset = true;
        window.coordinateSelectionActive = true;
        
        setFieldData(updatedFieldData);
        
        // Clear selection mode flags AFTER a delay to let the map update
        setTimeout(() => {
          setCoordinateSelectionMode(false);
          setSelectionPointInfo(null);
          window.coordinateSelectionActive = false;
          window.preventMapReset = false;
        }, 500);
      } else {
        // If field not found, clear flags immediately
        setCoordinateSelectionMode(false);
        setSelectionPointInfo(null);
        window.coordinateSelectionActive = false;
      }
      
    }
  };

  const handleAddFieldAtCenter = () => {
    const mapInstance = mapComponentRef.current?.mapInstanceRef?.current;
    if (!mapInstance) return;

    const center = mapInstance.getCenter();
    
    // Save current map view
    const currentZoom = mapInstance.getZoom();
    const currentCenter = mapInstance.getCenter();
    savedMapViewRef.current = {
      zoom: currentZoom,
      center: currentCenter
    };
    
    // Set prevention flags BEFORE creating new field
    window.preventMapReset = true;
    window.coordinateSelectionActive = true;
    window.coordinateEditorActive = true;
    
    // Create new field with test data and center point
    const newField = {
      name: 'Test', // Test name as requested
      product: 'Test', // Test product as requested  
      region: 'Test', // Test region as requested
      isNewTestField: true, 
      gpsPoints: [{
        latitude: center.lat,
        longitude: center.lng,
        timestamp: new Date().toISOString()
      }]
    };

    // Add to fieldData immediately (no toast message)
    const updatedFields = [...fieldData, newField];
    setFieldData(updatedFields);

    // Focus on the new field and open coordinate editor
    const newFieldIndex = updatedFields.length - 1;
    setFocusedFieldIndex(newFieldIndex);
    setEditingCoordinateField(newField);
    setCoordinateEditorOpen(true);
  };

  return (
    <>
    <Box p="md">
      <Group position="apart" mb="lg">
        <Title order={2}>
          <FormattedMessage id="mylist.navbar.myFarm" defaultMessage="My Farm" />
        </Title>
      </Group>
    </Box>
    <Container size="xl">
      
      <ResizableMapSection 
        mapurl={mapurl} 
        userAccountId={activeAccountId}
        useInteractiveMap={true}
        focusedFieldIndex={focusedFieldIndex}
        fieldData={fieldData}
        setFocusedFieldIndex={setFocusedFieldIndex}
        onOpenCoordinateEditor={(field, index) => {
          window.coordinateEditorActive = true;
          setEditingCoordinateField(field);
          setCoordinateEditorOpen(true);
        }}
        coordinateSelectionMode={coordinateSelectionMode}
        onCoordinateSelect={handleCoordinateSelect}  // <-- This was missing!
        selectionPointInfo={selectionPointInfo}
        onAddFieldAtCenter={handleAddFieldAtCenter}
        mapComponentRef={mapComponentRef}
      />

      {/* Improved Farm Fields Section */}
      <Paper padding="md" shadow="sm" style={{ marginTop: '15px', borderRadius: '8px', padding: '20px' }}>
        <Group position="apart" mb="md">
          <Group spacing="sm">
            <Title order={3} style={{ fontSize: '18px', margin: 0 }}>
              <FormattedMessage id="myfarm.fields.title" defaultMessage="Farm Fields" />
            </Title>
            <Badge color="teal" size="sm">
              {Object.keys(fieldData).length}
            </Badge>
          </Group>
          <Group spacing="xs">
            <Button 
              color="blue"
              variant="outline"
              onClick={() => setGeoJsonModalOpen(true)}
              disabled={fieldData.length === 0}
              size="sm"
              compact
            >
              <FormattedMessage id="myfarm.geojson.button.text" />
            </Button>
            <Button 
              color="teal" 
              onClick={handleAddField}
              leftIcon={<IconMapPin size={14} />}
              size="sm"
              compact
            >
              <FormattedMessage id="button.add.farm" />
            </Button>
          </Group>
        </Group>
        
        {/* Add Fields Section - MOVED TO TOP */}
        {showFieldSection && (
          <Paper shadow="sm" style={{ marginBottom: '25px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
            {/* Header */}
            <div style={{ backgroundColor: '#0d9488', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text color="white" weight={600} size="md">
                <FormattedMessage id="myfarm.add.field" defaultMessage="Add New Field" />
              </Text>
              <ActionIcon color="white" variant="transparent" onClick={() => setShowFieldSection(false)} title="Close">
                <IconX size={18} />
              </ActionIcon>
            </div>

            {farmFields.map((field, index) => (
              <div key={index} style={{ padding: '24px' }}>

                {/* Step 1 — Field Details */}
                <Group spacing="xs" mb="md">
                  <div style={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#0d9488', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>1</div>
                  <Text weight={600} size="sm">Field Details</Text>
                </Group>

                <Grid gutter="md" mb="xl">
                  <Grid.Col span={4}>
                    <TextInput
                      label={intl.formatMessage({ id:"myfarm.field.name" })}
                      placeholder={intl.formatMessage({ id:"myfarm.field.name" })}
                      value={field.name}
                      onChange={(e) => handleFieldChange(e, index, 'name')}
                      required
                      size="sm"
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label={intl.formatMessage({ id:"myfarm.field.product" })}
                      placeholder={intl.formatMessage({ id:"myfarm.field.product" })}
                      value={field.product}
                      onChange={(e) => handleFieldChange(e, index, 'product')}
                      size="sm"
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <TextInput
                      label={intl.formatMessage({ id:"myfarm.field.region" })}
                      placeholder={intl.formatMessage({ id:"myfarm.field.region" })}
                      value={field.region}
                      onChange={(e) => handleFieldChange(e, index, 'region')}
                      size="sm"
                    />
                  </Grid.Col>
                </Grid>

                <Divider mb="xl" />

                {/* Step 2 — GPS Location */}
                <Group spacing="xs" mb="md">
                  <div style={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#0d9488', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>2</div>
                  <Text weight={600} size="sm">GPS Location</Text>
                  {field.gpsPoints.length > 0 && (
                    <Badge color="teal" size="sm" variant="filled">{field.gpsPoints.length} point{field.gpsPoints.length > 1 ? 's' : ''} added</Badge>
                  )}
                </Group>

                {/* Method selector cards */}
                <Grid gutter="sm" mb="md">
                  <Grid.Col span={4}>
                    <div
                      onClick={() => { setManualEntry(false); setGmapsEntry(false); handleCollectGPS(index); }}
                      style={{ border: `2px solid ${!manualEntry && !gmapsEntry ? '#0d9488' : '#e5e7eb'}`, borderRadius: '8px', padding: '14px 12px', cursor: 'pointer', textAlign: 'center', backgroundColor: !manualEntry && !gmapsEntry ? '#f0fdfa' : 'white', transition: 'border-color 0.15s, background-color 0.15s' }}
                    >
                      <IconTarget size={22} color="#0d9488" style={{ marginBottom: 6 }} />
                      <Text size="xs" weight={600}>Auto-detect</Text>
                      <Text size="xs" color="dimmed" mt={2}>Use device GPS</Text>
                      {isLoadingGPS && <Loader size="xs" color="teal" mt={6} />}
                    </div>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <div
                      onClick={() => { setManualEntry(true); setGmapsEntry(false); }}
                      style={{ border: `2px solid ${manualEntry ? '#0d9488' : '#e5e7eb'}`, borderRadius: '8px', padding: '14px 12px', cursor: 'pointer', textAlign: 'center', backgroundColor: manualEntry ? '#f0fdfa' : 'white', transition: 'border-color 0.15s, background-color 0.15s' }}
                    >
                      <IconEdit size={22} color="#0d9488" style={{ marginBottom: 6 }} />
                      <Text size="xs" weight={600}>Manual entry</Text>
                      <Text size="xs" color="dimmed" mt={2}>Type lat / lng</Text>
                    </div>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <div
                      onClick={() => { setGmapsEntry(true); setManualEntry(false); }}
                      style={{ border: `2px solid ${gmapsEntry ? '#0d9488' : '#e5e7eb'}`, borderRadius: '8px', padding: '14px 12px', cursor: 'pointer', textAlign: 'center', backgroundColor: gmapsEntry ? '#f0fdfa' : 'white', transition: 'border-color 0.15s, background-color 0.15s' }}
                    >
                      <IconMapPin size={22} color="#0d9488" style={{ marginBottom: 6 }} />
                      <Text size="xs" weight={600}>Google Maps</Text>
                      <Text size="xs" color="dimmed" mt={2}>Paste a link</Text>
                    </div>
                  </Grid.Col>
                </Grid>

                {/* Google Maps input */}
                {gmapsEntry && (
                  <Grid gutter="md" mt="sm">
                    <Grid.Col span={10}>
                      <TextInput
                        label="Google Maps link"
                        placeholder="https://www.google.com/maps/@3.1234,5.6789,17z  or  maps.app.goo.gl/..."
                        value={gmapsLink}
                        onChange={(e) => setGmapsLink(e.target.value)}
                        size="sm"
                      />
                    </Grid.Col>
                    <Grid.Col span={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <Button color="teal" fullWidth onClick={() => handleSaveGmapsLink(index)} loading={isResolvingLink} size="sm">
                        Extract
                      </Button>
                    </Grid.Col>
                  </Grid>
                )}

                {/* Manual lat/lng input */}
                {manualEntry && (
                  <Grid gutter="md" mt="sm">
                    <Grid.Col span={5}>
                      <TextInput
                        label={intl.formatMessage({ id:"myfarm.field.manlat.label" })}
                        placeholder={intl.formatMessage({ id:"myfarm.field.manlat.lorem" })}
                        value={field.manualLat}
                        onChange={(e) => handleManualLatChange(e, index)}
                        size="sm"
                      />
                    </Grid.Col>
                    <Grid.Col span={5}>
                      <TextInput
                        label={intl.formatMessage({ id:"myfarm.field.manlong.label" })}
                        placeholder={intl.formatMessage({ id:"myfarm.field.manlong.lorem" })}
                        value={field.manualLng}
                        onChange={(e) => handleManualLngChange(e, index)}
                        size="sm"
                      />
                    </Grid.Col>
                    <Grid.Col span={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <Button color="teal" fullWidth onClick={() => handleSaveManualGPS(index)} disabled={!field.manualLat || !field.manualLng} size="sm">
                        <FormattedMessage id="button.add" defaultMessage="Add" />
                      </Button>
                    </Grid.Col>
                  </Grid>
                )}

                {/* Saved GPS points as chips */}
                {field.gpsPoints.length > 0 && (
                  <div style={{ marginTop: '16px', padding: '12px 14px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                    <Text size="xs" color="dimmed" mb="xs" weight={500}>Saved coordinates:</Text>
                    <Group spacing="xs">
                      {field.gpsPoints.map((pt, i) => (
                        <Badge key={i} color="teal" variant="light" size="sm" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                          #{i + 1} {pt.latitude?.toFixed(5)}, {pt.longitude?.toFixed(5)}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                )}

              </div>
            ))}

            {/* Footer */}
            {farmFields.length > 0 && (
              <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fafafa' }}>
                <Button variant="subtle" color="gray" onClick={() => setShowFieldSection(false)}>
                  <FormattedMessage id="button.cancel" />
                </Button>
                <Button
                  color="teal"
                  onClick={() => handleSave(farmFields)}
                  disabled={isSaveButtonDisabled}
                  leftIcon={<IconDeviceFloppy size={16} />}
                >
                  <FormattedMessage id="button.save" />
                </Button>
              </div>
            )}
          </Paper>
        )}
        
        {/* Search Bar */}
        <Group mb="md" style={{ position: 'relative', padding: '15px 0 25px 0' }}>
          <TextInput
            icon={<IconSearch size={16} />}
            placeholder={intl.formatMessage({ id: "myfarm.search.placeholder" })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="md"
            variant="unstyled"
            styles={{ input: { padding: '10px 14px 10px 40px' } }}
            rightSection={
              searchTerm ? (
                <ActionIcon onClick={() => setSearchTerm("")} size="lg" style={{ marginRight: '8px' }}>
                  <IconX size={16} />
                </ActionIcon>
              ) : null
            }
          />
        </Group>
        
        {/* No results message */}
        {filteredFields.length === 0 && (
          <Text color="dimmed" align="center" style={{ padding: '30px', fontSize: '16px' }}>
            <FormattedMessage id="myfarm.search.no.results" defaultMessage="No fields found matching your search" />
          </Text>
        )}
        
        {/* Field List - Table View */}
        <div>
          <Table striped highlightOnHover withBorder>
            <thead>
              <tr>
                <th><FormattedMessage id="myfarm.table.fieldname" defaultMessage="Field Name" /></th>
                <th><FormattedMessage id="myfarm.table.product" defaultMessage="Product" /></th>
                <th><FormattedMessage id="myfarm.table.region" defaultMessage="Region" /></th>
                <th><FormattedMessage id="myfarm.table.points" defaultMessage="Points" /></th>
                <th style={{ width: 130 }}><FormattedMessage id="myfarm.field.time" defaultMessage="Date" /></th>
                <th><FormattedMessage id="myfarm.table.certificates" defaultMessage="Certificates" /></th>
                <th style={{ width: 180 }}><FormattedMessage id="table.actions" defaultMessage="Actions" /></th>
              </tr>
            </thead>
            <tbody>
              {filteredFields.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <Center p="xl">
                      <Text color="dimmed">
                        <FormattedMessage id="myfarm.table.nofields" defaultMessage="No fields found" />
                      </Text>
                    </Center>
                  </td>
                </tr>
              ) : (
                tableRows.map(({ type, group, fieldName }) => type === 'year' ? (
                  <tr
                    key={`year-${group.label}`}
                    onClick={() => toggleYear(group.year)}
                    style={{ cursor: 'pointer', backgroundColor: '#f1f3f5' }}
                  >
                    <td colSpan={7}>
                      <Group spacing={8}>
                        {isYearOpen(group.year)
                          ? <IconChevronUp size={16} />
                          : <IconChevronDown size={16} />}
                        <Text size="sm" weight={600}>{group.label}</Text>
                        <Badge size="sm" color="gray" variant="light">{group.items.length}</Badge>
                      </Group>
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={fieldName}
                    style={{
                      // Compare against the fieldData index, not the row position - the map
                      // reports the index it holds, and rows are grouped and filtered.
                      backgroundColor: focusedFieldIndex === Number(fieldName) ? '#e3f2fd' : undefined
                    }}
                  >
                    <td style={{ maxWidth: '200px' }}>
                      <Text 
                        size="sm" 
                        weight={500}
                        style={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={fieldData[fieldName]?.name || fieldName}
                      >
                        {fieldData[fieldName]?.name || fieldName}
                      </Text>
                    </td>
                    <td>
                      <Text size="sm">{fieldData[fieldName]?.product || '-'}</Text>
                    </td>
                    <td>
                      <Text size="sm">{fieldData[fieldName]?.region || '-'}</Text>
                    </td>
                    <td>
                      <Center>
                        <Badge color="teal" variant="light" size="sm">
                          {fieldData[fieldName]?.gpsPoints?.length || 0}
                        </Badge>
                      </Center>
                    </td>
                    <td>
                      <Text size="sm" color="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        {formatFieldDate(fieldData[fieldName])}
                      </Text>
                    </td>
                    <td>
                      <Center>
                        <Group spacing={4}>
                          {fieldCertificatesRef.current[fieldData[fieldName]?.name] && (
                            <ActionIcon 
                              color="blue" 
                              onClick={() => downloadCertificate(fieldData[fieldName]?.name)}
                              title={intl.formatMessage({ id: 'myfarm.tooltip.certificate' })}
                              size="sm"
                            >
                              <FontAwesomeIcon icon={faFilePdf} />
                            </ActionIcon>
                          )}
                          
                          {fieldQRCodesRef.current[fieldData[fieldName]?.name] && (
                            <ActionIcon 
                              color="green" 
                              onClick={() => downloadQRCode(fieldData[fieldName]?.name)}
                              title={intl.formatMessage({ id: 'myfarm.tooltip.qrcode' })}
                              size="sm"
                            >
                              <IconQrcode size={14} />
                            </ActionIcon>
                          )}
                          
                          {/* Show loading or download button for EUDR */}
                          {isLoadingCertificates ? (
                            <ActionIcon 
                              color="orange" 
                              size="sm"
                              loading
                              disabled
                              title={intl.formatMessage({ id: 'myfarm.tooltip.checking' })}
                            >
                              <Loader size="xs" />
                            </ActionIcon>
                          ) : fieldEudrReportsRef.current[fieldData[fieldName]?.name] && (
                            <ActionIcon 
                              color="orange" 
                              onClick={() => downloadEudrReport(fieldData[fieldName]?.name)}
                              title={intl.formatMessage({ id: 'myfarm.tooltip.eudr' })}
                              size="sm"
                            >
                              <FontAwesomeIcon icon={faFilePdf} />
                            </ActionIcon>
                          )}
                        </Group>
                      </Center>
                    </td>
                    <td>
                      <Group spacing={4} noWrap>
                        {/* Only show generate button after loading is complete and no report exists */}
                        {!isLoadingCertificates && !fieldEudrReportsRef.current[fieldData[fieldName]?.name] && (
                          <ActionIcon 
                            color="orange" 
                            variant="filled"
                            size="sm"
                            onClick={() => generateEudrReport(fieldData[fieldName]?.name, fieldData[fieldName])}
                            loading={generatingEudrForField[fieldData[fieldName]?.name]}
                            disabled={generatingEudrForField[fieldData[fieldName]?.name]}
                            title={intl.formatMessage({ id: 'myfarm.tooltip.generateeudr' })}
                          >
                            <FontAwesomeIcon icon={faFilePdf} />
                          </ActionIcon>
                        )}
                        <ActionIcon 
                          color="teal" 
                          variant="light"
                          onClick={() => handleFocusField(fieldName)}
                          size="sm"
                          title={intl.formatMessage({ id: "map.focus.title" })}
                        >
                          <IconMapPin size={16} />
                        </ActionIcon>
                        <ActionIcon
                          color="orange"
                          variant="light"
                          onClick={() => {
                            setEditingCoordinateField(fieldData[fieldName]);
                            setCoordinateEditorOpen(true);
                          }}
                          size="sm"
                          title={intl.formatMessage({ id: "myfarm.tooltip.editcoordinates" })}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon 
                          color="red" 
                          variant="light"
                          onClick={() => handleRemoveField(fieldName)}
                          size="sm"
                          title={intl.formatMessage({ id: "myfarm.tooltip.delete" })}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
        
        {/* Compact Documents Section - Collapsible */}
        <Accordion mt="md" variant="contained">
          <Accordion.Item value="documents">
            <Accordion.Control>
              <Group spacing="xs">
                <Text size="sm" weight={500}>
                  <FormattedMessage id="myfarm.documents.title" defaultMessage="Documents" />
                </Text>
                <Badge size="xs" color="gray">
                  {userDocuments.length}
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <div style={{ maxHeight: '200px', overflow: 'auto' }}>
              <Group position="apart" mb="md">
                <Button 
                  color="orange"
                  variant="outline"
                  onClick={() => setDocumentUploadModalOpen(true)}
                  leftIcon={<IconUpload size={14} />}
                  size="sm"
                  compact
                >
                  <FormattedMessage id="myfarm.upload.documents" defaultMessage="Upload Documents" />
                </Button>
              </Group>
              <Group spacing="xs">
                {userDocuments.map((doc, index) => (
                    <Badge
                      key={index}
                      size="md"
                      variant="outline"
                      style={{ cursor: 'pointer', padding: '8px 12px' }}
                      onClick={() => openDocument(doc.url)}
                      title={doc.name}
                    >
                      {doc.type?.toUpperCase() || 'FILE'}: {doc.name.length > 20 ? doc.name.substring(0, 20) + '...' : doc.name}
                    </Badge>
                  ))}
                  {userDocuments.length === 0 && (
                    <Text color="dimmed" size="sm">
                      <FormattedMessage id="myfarm.documents.empty" defaultMessage="No documents uploaded yet" />
                    </Text>
                  )}
                </Group>
              </div>
            </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
      </Paper>

      <RemoveFieldConfirmationModal
        fieldName={fieldToRemove}
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
        opened={isConfirmationModalOpen}
      />
      <GeoJSONModal
        opened={geoJsonModalOpen}
        onClose={() => {
          setGeoJsonModalOpen(false);
          setGeoJsonParams({ company: '', country: '' });
        }}
        geoJsonParams={geoJsonParams}
        setGeoJsonParams={setGeoJsonParams}
        generateGeoJSON={generateGeoJSON}
        showToast={showToast}
      />
      <CoordinateEditorModal
        opened={coordinateEditorOpen}
        onClose={() => {
          // Cancel any ongoing certificate/EUDR loading
          if (loadCertificatesAbortController.current) {
            loadCertificatesAbortController.current.abort();
          }
          
          // Just close the modal and clear flags - don't remove any fields
          window.coordinateEditorActive = false;
          window.preventMapReset = false;
          window.coordinateSelectionActive = false;
          setCoordinateEditorOpen(false);
          setSelectedPointForMap(null);
        }}
        fieldData={editingCoordinateField}
        accountId={activeAccountId}
        onSelectCoordinateFromMap={handleSelectCoordinateFromMap}
        selectedPointIndex={selectedPointForMap}
        onSelectPointForMap={setSelectedPointForMap}
      onSave={(newPoints, updatedFieldData) => {  // <-- THIS IS THE onSave CALLBACK
        const fieldIndex = fieldData.findIndex(field => field.name === editingCoordinateField.name);
        
        if (fieldIndex !== -1) {
          // IMPORTANT: Set flag to prevent map zoom reset during coordinate updates
          window.preventMapReset = true;
          
          // Remove the temporary flag before saving
          const cleanedFieldData = { ...updatedFieldData };
          delete cleanedFieldData.isNewTestField;
          
          // Update fieldData immediately for instant visual feedback
          const updatedFields = [...fieldData];
          updatedFields[fieldIndex] = cleanedFieldData;
          setFieldData(updatedFields); // Immediate map update

          // Then save to backend
          const dataToSave = {
            userId: userinfo?.userId,
            userAccountId: activeAccountId,
            farmFields: updatedFields,
          };

          addFieldAPI(dataToSave)
            .then((response) => {
              if (response.status === "Success") {
                showToast('success', intl.formatMessage({ id: "myfarm.toast.success" }));
                
                // Refresh from backend to ensure consistency
                getFieldAPI({ accountId: activeAccountId })
                  .then((data) => {
                    if (Array.isArray(data.result)) {
                      // Set flag again to prevent zoom reset on backend refresh
                      window.preventMapReset = true;
                      setFieldData(data.result);
                      setTimeout(() => {
                        window.preventMapReset = false;
                      }, 500);
                    }
                  })
                  .catch((error) => {
                    console.error('Error fetching updated data:', error);
                    // Don't revert if refresh fails, keep the optimistic update
                    setTimeout(() => {
                      window.preventMapReset = false;
                    }, 500);
                  });
              } else {
                showToast('error', intl.formatMessage({ id: "myfarm.toast.error" }));
                // Revert on error
                getFieldAPI(userFullDetails)
                  .then((data) => {
                    if (Array.isArray(data.result)) {
                      setFieldData(data.result);
                    }
                  })
                  .catch((err) => {
                    console.error('Error reverting data:', err);
                  })
                  .finally(() => {
                    window.preventMapReset = false;
                  });
              }
            })
            .catch((error) => {
              console.error('Error updating coordinates:', error);
              
              // Determine error message based on error type
              let errorMessage = intl.formatMessage({ id: "myfarm.toast.apierror" });
              if (error.message === 'Failed to fetch') {
                errorMessage = 'Network error: Unable to connect to server. Please check your connection.';
              } else if (error.message.includes('HTTP error')) {
                errorMessage = `Server error: ${error.message}`;
              }
              
              showToast('error', errorMessage);
              
              // Try to revert, but keep optimistic update if revert fails
              getFieldAPI(userFullDetails)
                .then((data) => {
                  if (Array.isArray(data.result)) {
                    setFieldData(data.result);
                  }
                })
                .catch((err) => {
                  console.error('Error reverting data:', err);
                  // Keep the optimistic update since we can't revert
                })
                .finally(() => {
                  window.preventMapReset = false;
                });
            });
        }
      }}

      />
      
      {/* Document Upload Modal */}
      <Modal
        opened={documentUploadModalOpen}
        onClose={() => setDocumentUploadModalOpen(false)}
        title={<FormattedMessage id="myfarm.upload.modal.title" defaultMessage="Upload Documents" />}
        size="lg"
      >
        <div style={{ maxHeight: '500px', overflow: 'auto' }}>
        <Center mb="md">
          <DropzoneButton onUploadSuccess={() => {
            fetchUserDocuments();
            showToast('success', intl.formatMessage({ id: 'myfarm.upload.success', defaultMessage: 'Document uploaded successfully' }));
          }} />
        </Center>
        
        {isLoadingDocuments ? (
          <Center p="xl">
            <Loader size="md" />
          </Center>
        ) : (
          <div>
            <Text size="sm" weight={500} mb="sm">
              <FormattedMessage id="myfarm.documents.uploaded" defaultMessage="Your Uploaded Documents" />
            </Text>
            {userDocuments.length === 0 ? (
              <Text color="dimmed" align="center" p="xl">
                <FormattedMessage id="myfarm.documents.empty" defaultMessage="No documents uploaded yet" />
              </Text>
            ) : (
              <Table striped>
                <thead>
                  <tr>
                    <th><FormattedMessage id="myfarm.documents.table.name" defaultMessage="File Name" /></th>
                    <th><FormattedMessage id="myfarm.documents.table.type" defaultMessage="Type" /></th>
                    <th><FormattedMessage id="myfarm.documents.table.actions" defaultMessage="Actions" /></th>
                  </tr>
                </thead>
                <tbody>
                  {userDocuments.map((doc, index) => (
                    <tr key={index}>
                      <td>{doc.name}</td>
                      <td>
                        {doc.name.endsWith('.pdf') && <FontAwesomeIcon icon={faFilePdf} color="#dc3545" />}
                        {(doc.name.endsWith('.xls') || doc.name.endsWith('.xlsx')) && <FontAwesomeIcon icon={faFileExcel} color="#217346" />}
                        {(!doc.name.endsWith('.pdf') && !doc.name.endsWith('.xls') && !doc.name.endsWith('.xlsx')) && <FontAwesomeIcon icon={faFile} />}
                      </td>
                      <td>
                        <Group spacing="xs">
                          <ActionIcon 
                            color="blue"
                            variant="subtle"
                            onClick={() => window.open(doc.url, '_blank')}
                          >
                            <IconExternalLink size={16} />
                          </ActionIcon>
                        </Group>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}
        </div>
      </Modal>
    </Container>
    </>
  );
}